import { buildOrgUrl, getOrgSlugFromHostname } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { searchProjectsQuery } from '@/queries/search.query';
import { useQuery } from '@tanstack/react-query';
import { FolderOpen, Loader2 } from 'lucide-react';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GlobalSearch: FunctionComponent<GlobalSearchProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['searchProjects', debouncedSearch],
    queryFn: () => execute(searchProjectsQuery, { query: debouncedSearch }),
    enabled: open && debouncedSearch.length >= 2,
  });

  const currentSlug = getOrgSlugFromHostname();
  const results = data?.searchProjects ?? [];

  const grouped = useMemo(() => {
    const currentOrg: typeof results = [];
    const otherOrgs = new Map<string, { name: string; items: typeof results }>();

    for (const result of results) {
      if (result.organizationSlug === currentSlug) {
        currentOrg.push(result);
      } else {
        const key = result.organizationSlug;
        if (!otherOrgs.has(key)) {
          otherOrgs.set(key, { name: result.organizationName, items: [] });
        }
        otherOrgs.get(key)!.items.push(result);
      }
    }

    return { currentOrg, otherOrgs };
  }, [results, currentSlug]);

  const handleSelect = useCallback(
    (id: string, slug: string) => {
      onOpenChange(false);
      if (slug === currentSlug) {
        navigate(`/projects/${id}`);
      } else {
        window.open(buildOrgUrl(slug, `/projects/${id}`));
      }
    },
    [currentSlug, navigate, onOpenChange],
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title='Search Projects'
      description='Search across all your projects'
      showCloseButton={false}>
      <Command shouldFilter={false}>
        <CommandInput placeholder='Search projects...' value={search} onValueChange={setSearch} />
        <CommandList>
          {debouncedSearch.length < 2 && <CommandEmpty>Start typing to search projects...</CommandEmpty>}

          {debouncedSearch.length >= 2 && !isLoading && (results.length === 0 || isError) && (
            <CommandEmpty>No projects found.</CommandEmpty>
          )}

          {isLoading && (
            <div className='flex items-center justify-center py-6'>
              <Loader2 className='size-4 animate-spin' />
            </div>
          )}

          {grouped.currentOrg.length > 0 && (
            <CommandGroup heading='Current Organization'>
              {grouped.currentOrg.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.id}
                  className='py-1.5'
                  onSelect={() => handleSelect(result.id, result.organizationSlug)}>
                  <FolderOpen className='size-4 shrink-0' />
                  <div className='flex flex-col gap-0 min-w-0'>
                    <span className='truncate'>{result.name}</span>
                    {result.description && (
                      <span className='text-muted-foreground text-xs truncate'>{result.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {[...grouped.otherOrgs.entries()].map(([slug, { name, items }]) => (
            <CommandGroup key={slug} heading={name}>
              {items.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.id}
                  className='py-1.5'
                  onSelect={() => handleSelect(result.id, result.organizationSlug)}>
                  <FolderOpen className='size-4 shrink-0' />
                  <div className='flex flex-col gap-0 min-w-0'>
                    <span className='truncate'>{result.name}</span>
                    {result.description && (
                      <span className='text-muted-foreground text-xs truncate'>{result.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    </CommandDialog>
  );
};

export default GlobalSearch;
